import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Camera, Award, Users, Globe, ArrowRight } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';

const DEFAULT_ICONS = [Camera, Award, Users, Globe];

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const { localize } = useTranslation();
  const settings = useAdminStore((s) => s.settings);
  const aboutSection = useAdminStore((s) => s.settings?.homeAboutSection);
  const ap = useAdminStore((s) => s.settings?.aboutPageContent);
  const sectionVis = useAdminStore((s) => s.settings?.sectionVisibility);
  const showFooter = sectionVis?.aboutFooter !== false;
  const showValues = sectionVis?.aboutValues !== false;
  const showStats = sectionVis?.aboutStats !== false;
  const allArtworks = useAdminStore((s) => s.artworks);
  const teamMembers = useAdminStore((s) => s.teamMembers);
  const language = localize({ en: 'en', ka: 'ka' });

  const galleryName = localize(settings?.galleryName) || 'Gallery Fotoatelier';
  const description = localize(aboutSection?.description) ||
    "Gallery Fotoatelier is Tbilisi's premier fine art photography gallery, dedicated to showcasing exceptional contemporary works from Georgian and international artists.";

  const pageTitle = localize(ap?.pageTitle) || (language === 'ka' ? 'ჩვენს შესახებ' : 'About Us');
  const secondParagraph = localize(ap?.secondParagraph) || (language === 'ka'
    ? 'ჩვენი გალერეა წარმოადგენს კულტურულ ხიდს, რომელიც აკავშირებს ქართული ვიზუალური ხელოვნების მდიდარ ტრადიციას საერთაშორისო თანამედროვე ფოტოგრაფიის სცენასთან.'
    : 'Our gallery serves as a cultural bridge, connecting the rich tradition of Georgian visual arts with the international contemporary photography scene. Through carefully curated exhibitions, art fairs, and collaborations, we provide a platform for emerging and established artists to share their vision with the world.');

  const valuesLabel = localize(ap?.valuesLabel) || (language === 'ka' ? 'რას ვიცავთ' : 'What We Stand For');
  const valuesTitle = localize(ap?.valuesTitle) || (language === 'ka' ? 'ჩვენი ღირებულებები' : 'Our Values');

  const defaultValues = [
    { title: language === 'ka' ? 'სახელოვნებო ფოტოგრაფია' : 'Fine Art Photography', desc: language === 'ka' ? 'ჩვენ ვაკურირებთ განსაკუთრებულ თანამედროვე ფოტოგრაფიულ ნამუშევრებს.' : 'We curate exceptional contemporary photographic works that push boundaries and inspire dialogue.' },
    { title: language === 'ka' ? 'სრულყოფილება' : 'Excellence', desc: language === 'ka' ? 'ჩვენი კოლექციის ყოველი ნამუშევარი აკმაყოფილებს მხატვრული და ტექნიკური ხარისხის უმაღლეს სტანდარტებს.' : 'Every piece in our collection meets the highest standards of artistic and technical quality.' },
    { title: language === 'ka' ? 'თემი' : 'Community', desc: language === 'ka' ? 'ჩვენ ვქმნით კავშირებს არტისტებს, კოლექციონერებსა და ხელოვნების მოყვარულებს შორის.' : 'We foster connections between artists, collectors, and art enthusiasts from around the world.' },
    { title: language === 'ka' ? 'საერთაშორისო ხედვა' : 'International Vision', desc: language === 'ka' ? 'ქართული ხელოვნების შეერთება გლობალურ თანამედროვე არტ სცენასთან.' : 'Bridging Georgian artistry with the global contemporary art scene through exhibitions and fairs.' },
  ];

  const values = ap?.values?.length
    ? ap.values.map((v, i) => ({ title: localize(v.title), desc: localize(v.description), icon: DEFAULT_ICONS[i % DEFAULT_ICONS.length] }))
    : defaultValues.map((v, i) => ({ ...v, icon: DEFAULT_ICONS[i] }));

  const ctaTitle = localize(ap?.ctaTitle) || (language === 'ka' ? 'ეწვიეთ ჩვენს გალერეას' : 'Visit Our Gallery');
  const ctaSubtitle = localize(ap?.ctaSubtitle) || (language === 'ka' ? 'პირადად გაეცანით ჩვენს კოლექციას. ჩვენ მივესალმებით ხელოვნების მოყვარულებს, კოლექციონერებსა და ცნობისმოყვარეებს.' : 'Experience our collection in person. We welcome art enthusiasts, collectors, and curious minds alike.');
  const ctaBtn1 = localize(ap?.ctaButton1) || (language === 'ka' ? 'დაგვიკავშირდით' : 'Contact Us');
  const ctaBtn2 = localize(ap?.ctaButton2) || (language === 'ka' ? 'კოლექციის ნახვა' : 'Browse Collection');

  const stats = [
    { value: allArtworks.length.toString(), label: language === 'ka' ? 'ნამუშევრები' : 'Artworks' },
    { value: teamMembers.length.toString(), label: language === 'ka' ? 'გუნდი' : 'Team Members' },
    { value: language === 'ka' ? 'თბილისი' : 'Tbilisi', label: language === 'ka' ? 'ლოკაცია' : 'Location' },
    { value: '2024', label: language === 'ka' ? 'დაარსდა' : 'Founded' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-black transition-colors duration-700">
      {/* Hero */}
      <section className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6 md:px-24">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-4xl sm:text-6xl md:text-8xl font-syncopate font-bold text-black dark:text-white uppercase tracking-tighter leading-none">
              {pageTitle}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* About Content + Image */}
      <section className="px-4 sm:px-6 md:px-24 pb-20 sm:pb-32">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row gap-12 md:gap-20 items-center">
          <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="flex-1 space-y-6">
            <h2 className="text-2xl sm:text-4xl font-syncopate font-bold tracking-tighter text-black dark:text-white uppercase leading-tight">
              {localize(aboutSection?.title) || 'Fine Art Photography'}
            </h2>
            <p className="text-black/50 dark:text-white/50 text-base sm:text-lg font-light leading-relaxed max-w-lg">
              {description}
            </p>
            {secondParagraph && (
              <p className="text-black/40 dark:text-white/40 text-sm font-light leading-relaxed max-w-lg">
                {secondParagraph}
              </p>
            )}
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 1 }} className="flex-1 w-full max-w-md">
            {aboutSection?.image ? (
              <img src={aboutSection.image} alt={galleryName} className="w-full aspect-[4/3] object-cover rounded-2xl shadow-2xl shadow-black/10" />
            ) : (
              <div className="w-full aspect-[4/3] rounded-2xl bg-[#f5f5f5] dark:bg-[#111] flex items-center justify-center">
                <Camera size={48} className="text-black/10 dark:text-white/10" />
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      {showStats && (
      <section className="py-16 sm:py-20 bg-[#fafafa] dark:bg-[#0a0a0a] border-y border-black/5 dark:border-white/5 transition-colors">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, i) => (
              <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.6 }} className="text-center">
                <p className="text-3xl sm:text-4xl font-syncopate font-bold tracking-tighter text-black dark:text-white">{stat.value}</p>
                <p className="text-[9px] font-bold uppercase tracking-[0.25em] text-black/30 dark:text-white/30 mt-2">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      )}

      {/* Values */}
      {showValues && values.length > 0 && (
        <section className="py-20 sm:py-32 px-4 sm:px-6 md:px-24">
          <div className="max-w-6xl mx-auto">
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-12 sm:mb-16">
              <span className="text-[9px] font-bold tracking-[0.5em] text-black/20 dark:text-white/20 uppercase mb-3 block">{valuesLabel}</span>
              <h2 className="text-2xl sm:text-4xl font-syncopate font-bold tracking-tighter text-black dark:text-white uppercase">{valuesTitle}</h2>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {values.map((v, i) => {
                const Icon = v.icon;
                return (
                  <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.6 }} className="p-6 sm:p-8 rounded-2xl border border-black/5 dark:border-white/5 bg-[#fafafa] dark:bg-[#0a0a0a] hover:border-black/10 dark:hover:border-white/10 transition-all group">
                    <div className="w-12 h-12 rounded-xl bg-black/5 dark:bg-white/5 flex items-center justify-center mb-5 group-hover:bg-black group-hover:text-white dark:group-hover:bg-white dark:group-hover:text-black transition-all duration-300">
                      <Icon size={22} />
                    </div>
                    <h3 className="text-sm font-bold uppercase tracking-wider text-black dark:text-white mb-2">{v.title}</h3>
                    <p className="text-xs text-black/40 dark:text-white/40 font-light leading-relaxed">{v.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA (Footer) */}
      {showFooter && (
      <section className="py-16 sm:py-20 px-4 sm:px-6 md:px-24 bg-black dark:bg-white transition-colors">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl font-syncopate font-bold tracking-tighter text-white dark:text-black uppercase mb-4">{ctaTitle}</h2>
          <p className="text-white/50 dark:text-black/50 text-sm mb-8 max-w-md mx-auto">{ctaSubtitle}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <button onClick={() => navigate('/contact')} className="flex items-center gap-2 px-8 py-3.5 bg-white dark:bg-black text-black dark:text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:opacity-90 transition-opacity">
              {ctaBtn1} <ArrowRight size={14} />
            </button>
            <button onClick={() => navigate('/shop')} className="flex items-center gap-2 px-8 py-3.5 border border-white/20 dark:border-black/20 text-white dark:text-black text-[10px] font-bold uppercase tracking-[0.15em] rounded-xl hover:border-white/40 dark:hover:border-black/40 transition-colors">
              {ctaBtn2} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>
      )}
    </div>
  );
};

export default AboutPage;
